/**
 * First-load-only redirect: if the homepage ("/") is the very first page of
 * this browsing session and the user is LOGGED IN with a cohort on their
 * account, send them straight to that cohort's page instead of showing "/".
 *
 * Guests (not logged in) always see the real homepage on first load, no
 * matter what — even if localStorage has leftover selectedExamName/
 * preferences data from a previous login session (e.g. the session simply
 * expired without going through logout()'s cleanup). Client-side JS can't
 * reliably tell "this visitor's own guest pick" apart from "stale data left
 * behind by whoever was logged in before", so a logged-out visitor must never
 * be bounced to someone else's cohort — those stale keys are wiped instead.
 *
 * Any later visit to "/" in the same session must show the real homepage —
 * even for a logged-in user who explored a different cohort mid-session.
 * exam-display-utils.js's resetSessionOverrideOnHome() already resets the
 * header trigger back to the account cohort in that case; this script just
 * needs to not redirect the browser away before that gets a chance to run.
 *
 * Loaded as a plain (non-deferred) <script> right after window.hasCustomer /
 * window.__ACCOUNT_COHORT__ near the very top of <head> in theme.liquid — as
 * early as possible so it runs before any page content parses/paints (no
 * homepage flash). window.__ACCOUNT_COHORT__ is a minimal, early-rendered
 * subset of customer cohort data; it exists solely for this script, since the
 * fuller window.__CUSTOMER_DATA__ (gtm-global-variables.liquid) isn't set
 * until deep in <body>, too late to avoid the flash.
 */
(function () {
  "use strict";

  try {
    var SESSION_FLAG_KEY = "pwHomeCohortRedirectChecked";
    var isFirstLoadOfSession = window.sessionStorage.getItem(SESSION_FLAG_KEY) !== "true";
    if (isFirstLoadOfSession) {
      window.sessionStorage.setItem(SESSION_FLAG_KEY, "true");
    }

    if (!isFirstLoadOfSession) return;
    if (window.location.pathname !== "/") return;

    function slugify(name) {
      return String(name || "")
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "-");
    }

    function fromPreferences(raw) {
      var prefs = raw;
      if (typeof raw === "string") {
        try {
          prefs = JSON.parse(raw);
        } catch (e) {
          return null;
        }
      }
      if (!prefs || !prefs.exams || !prefs.exams.length) return null;
      var last = prefs.exams[prefs.exams.length - 1];
      var name = typeof last === "string" ? last.trim().replace(/-/g, " ") : "";
      if (!name) return null;
      return {
        name: name,
        icon: (prefs.examIcon && String(prefs.examIcon)) || "",
        url: (prefs.redirection_url && String(prefs.redirection_url).trim()) || null,
      };
    }

    var isLoggedIn = window.hasCustomer === true;

    if (!isLoggedIn) {
      // Not logged in -> always real homepage, and scrub any stale cohort
      // data left behind by a prior login session so the header trigger
      // doesn't keep showing someone else's cohort either.
      try {
        localStorage.removeItem("selectedExamName");
        localStorage.removeItem("selectedExamIcon");
        localStorage.removeItem("selectedExamRedirectUrl");
        localStorage.removeItem("preferences");
        sessionStorage.removeItem("user_data");
      } catch (e) {}
      return;
    }

    // Logged in: locally saved preference (this account's own selection) ->
    // selectedExamName fallback -> account cohort from server metafields
    // (covers a fresh browser/device where localStorage is empty but the
    // logged-in customer's account already has a cohort).
    var cohort = null;
    try {
      cohort = fromPreferences(localStorage.getItem("preferences"));
    } catch (e) {}

    if (!cohort) {
      var selectedName = null;
      var selectedUrl = null;
      try {
        selectedName = localStorage.getItem("selectedExamName");
        selectedUrl = localStorage.getItem("selectedExamRedirectUrl");
      } catch (e) {}
      if (selectedName && selectedName.trim()) {
        cohort = { name: selectedName.trim(), icon: "", url: selectedUrl || null };
      }
    }

    if (!cohort && window.__ACCOUNT_COHORT__) {
      cohort = fromPreferences(window.__ACCOUNT_COHORT__.preferences);
      if (!cohort && window.__ACCOUNT_COHORT__.cohort_name) {
        cohort = { name: String(window.__ACCOUNT_COHORT__.cohort_name).trim(), icon: "", url: null };
      }
    }

    if (!cohort || !cohort.name) return; // logged in but no cohort yet -> stays on real homepage

    var redirectUrl = cohort.url || "/pages/" + slugify(cohort.name);
    if (!redirectUrl || redirectUrl === "/") return;

    try {
      localStorage.setItem("selectedExamName", cohort.name);
      if (cohort.icon) localStorage.setItem("selectedExamIcon", cohort.icon);
      localStorage.setItem("selectedExamRedirectUrl", redirectUrl);
    } catch (e) {}

    window.location.replace(redirectUrl);
  } catch (e) {}
})();
